import { brandTextColor } from "@/utils/brandContrast";
// @see https://gist.github.com/vielhuber/afcb99fdf23febffe0199649e19642cb
// @see https://gist.github.com/vielhuber

import format from "date-fns/format";
import { formatInTimeZone } from "date-fns-tz";
import _ from "lodash";
import i18n from "../plugins/vue-i18n";
import { getNormalizedOaicCompletion } from "./oaicCompletion";
import settings from "./settings";
import {
  getFallbackLanguageKey,
  getLocaleCodeByLanguageKey,
} from "./language";

const I18N_TRANSLATE_ENABLED = true;

// typescript dynamic key value object
// @see https://www.codegrepper.com/code-examples/typescript/typescript+dynamic+object+keys
interface LooseObject {
  [key: string]: any;
}

export const DEFAULT_TIMEZONE = "Asia/Bangkok";
export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd HH:mm:ss";

const helpers = {
  getLanguages(): string[] {
    return settings.I18N_AVAILABLE_LANGUAGES.map((language) => language.key);
  },
  getLocaleCodes(): string[] {
    return settings.I18N_AVAILABLE_LANGUAGES.map((lang) => lang.locale);
  },
  getLocale(): string {
    const lang = this.getLanguage();
    const found = settings.I18N_AVAILABLE_LANGUAGES.find((l) => l.key === lang);
    return found?.locale || "en_US";
  },
  getLanguage(): string {
    return i18n.getLocale() || "en";
  },
  now(): string {
    return formatInTimeZone(new Date(), DEFAULT_TIMEZONE, DEFAULT_DATE_FORMAT);
  },
  guid(): string {
    return format(new Date(), "yyyyMMddHHmmss");
  },
  isSafari(): boolean {
    // @see https://burningthumb.com/safari-javascript-date-format/
    // @see https://www.geeksforgeeks.org/how-to-detect-the-user-browser-safari-chrome-ie-firefox-and-opera-using-javascript/
    const userAgentString = navigator.userAgent || "";
    const chromeAgent = userAgentString.indexOf("Chrome") > -1;
    let safariAgent = userAgentString.indexOf("Safari") > -1;
    if (chromeAgent && safariAgent) safariAgent = false;
    return safariAgent;
  },
  isTh(): boolean {
    return i18n.getLocale() == "th";
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  t(text: string, variables?: any): string {
    if (!I18N_TRANSLATE_ENABLED) return text;
    return i18n.t(text, variables);
  },
  objectLang(obj: { label?: string }, attr: string): string {
    if (obj == null) return "";
    const lang = i18n.getLocale() || "en";
    let label = "";
    const origAttr = attr;
    const value = _.get(obj, attr);
    if (_.isString(value)) {
      if (lang != "en") {
        attr = attr + "_" + lang;
      }
      if (Object.prototype.hasOwnProperty.call(obj, attr)) {
        label = (obj as any)[attr];
      }
      // fallback to english (original attribute)
      if (!label) {
        label = _.get(obj, origAttr, "");
      }
    } else if (_.isObject(value)) {
      const locale = getLocaleCodeByLanguageKey(lang);
      if (_.has(value, locale) || _.has(value, lang)) {
        label = _.get(value, locale, "") || _.get(value, lang, "");
      } else {
        const attributes = Object.keys(value);
        
        // filter attributes that have configure in this project
        const filteredAttributes = attributes.filter((attribute) => settings.I18N_AVAILABLE_LANGUAGES.some((language) => attribute.startsWith(language.key)));
        
        const fallbackLocaleCode = getLocaleCodeByLanguageKey(
          getFallbackLanguageKey()
        );
        label = _.get(value, fallbackLocaleCode, "") || _.get(value, getFallbackLanguageKey(), "");
        if (!label && filteredAttributes.length > 0) {
          label = _.get(value, filteredAttributes[0], "");
        }
      }
    } else {
      label = "";
    }
    return label;
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  lang(obj: any, attr: string): string {
    if (obj == null) return "";
    const origAttr = attr;
    const lang = i18n.getLocale() || "en";
    let label = "";
    const value = _.get(obj, attr, "");
    if (_.isString(value)) {
      if (lang != "en") {
        attr = attr + "_" + lang;
      }
      if (Object.prototype.hasOwnProperty.call(obj, attr)) {
        label = (obj as any)[attr];
      }
      if (!label) {
        label = _.get(obj, origAttr, "");
      }
    } else if (_.isObject(value)) {
      label = this.objectLang(obj, attr);
    }
    return label;
  },
  /** Acceptable behaviors custom row: `label` is locale map { en_US, th_TH, … } or legacy string / labelEN */
  acceptableBehaviorCustomLabel(item: {
    label?: unknown;
    labelEN?: string;
  }): string {
    if (item == null) return "";
    const raw = item.label;
    if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
      const localized = raw as Record<string, string>;
      const currentLang = i18n.getLocale() || "en";
      const currentLocaleCode = getLocaleCodeByLanguageKey(currentLang);

      // Prefer exact locale currently used by user (e.g. fr_FR).
      if (_.get(localized, currentLocaleCode, "")) {
        return _.get(localized, currentLocaleCode, "");
      }

      // Also support cases where locale key itself is stored directly (e.g. "fr").
      if (_.get(localized, currentLang, "")) {
        return _.get(localized, currentLang, "");
      }

      // Fallback: first available locale value in object.
      const firstLocaleKey = Object.keys(localized)[0];
      if (firstLocaleKey) {
        return _.get(localized, firstLocaleKey, "");
      }
      return "";
    }
    if (typeof raw === "string" && raw) return raw;
    return (item.labelEN as string) || "";
  },
  acceptableBehaviorCustomTitleParts(item: {
    label?: unknown;
    labelEN?: string;
    labelTH?: string;
  }): { title: string; title_th: string } {
    const raw = item?.label;
    if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
      const o = raw as Record<string, string>;
      const currentLang = i18n.getLocale() || "en";
      const currentLocaleCode = getLocaleCodeByLanguageKey(currentLang);
      return {
        title:
          _.get(o, currentLocaleCode) ||
          _.get(o, currentLang) ||
          _.get(o, "en_US") ||
          _.get(o, Object.keys(o)[0]) ||
          "",
        title_th: _.get(o, "th_TH") || "",
      };
    }
    return {
      title: (typeof raw === "string" ? raw : item?.labelEN) || "",
      title_th: (item?.labelTH as string) || "",
    };
  },
  dateFormat(datetime: string, dateFormat = "MMMM d, y"): string {
    if (!datetime) return "";
    if (this.isSafari()) {
      datetime = datetime.replace(/-/g, "/");
    }
    const result = format(new Date(datetime), dateFormat);
    return result;
  },
  getCurrency(): string {
    const currency = "USD";
    return currency;
  },
  getOmisePriceFormat(realPrice: string): number {
    realPrice =
      realPrice.substring(0, realPrice.length - 2) +
      "." +
      realPrice.substring(realPrice.length - 2, realPrice.length);
    return parseFloat(realPrice);
  },
  getPriceBySku(sku: string, format = "default"): any {
    let price = 0;
    if (sku == "Unlimited_USD") {
      price = 499;
    } else if (sku == "Unlimited_THB") {
      price = 17000;
    } else {
      const chunks = sku.split("_");
      // const credit = chunks[1];
      price = this.getOmisePriceFormat(chunks[2]);
    }

    // Omise Price Format
    if (format.toLowerCase() == "omise") {
      price *= 100;
    }

    return price;
  },
  getDescriptionBySku(sku: string, currency: string): string {
    const price = this.getPriceBySku(sku, "omise");
    if (sku.indexOf("Unlimited") === 0) {
      return `${sku}x1=${price}${currency}`.toUpperCase();
    }
    // const chunks = sku.split("_");
    // const credit = parseInt(chunks[1]);
    // const currency = this.getCurrency();
    // const description = `${sku}x1=${price}${currency}`;
    const description = sku;
    return description;
  },
  workflowStatus(status: string): string {
    let label = status;
    if (status == "INVITED") {
      label = "Invited";
    } else if (status == "TODO") {
      label = "Started";
    } else if (status == "IN_PROGRESS") {
      label = "Incompleted";
    } else if (status == "COMPLETED") {
      label = "Completed";
    }
    return label;
  },
  // The Fisher-Yates algorith
  // @see https://dev.to/codebubb/how-to-shuffle-an-array-in-javascript-2ikj
  shuffleArray(array: any[]): any {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }
  },
  randomNumber(digits = 6): number {
    // @see https://www.codegrepper.com/code-examples/javascript/generate+6+digit+random+number+in+node+js
    const base = Math.pow(10, digits - 1);
    return Math.floor(base + Math.random() * (9 * base));
  },
  generateString(length = 5): string {
    // @see https://www.programiz.com/javascript/examples/generate-random-strings
    // declare all characters
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  },
  filterEmail(value: string): string {
    value = String(value).toLowerCase();
    return value;
  },
  filterPath(path: string): string {
    if (!path) return "";
    // Firebase RTDB keys cannot contain . # $ [ ] /
    return path
      .replace(/\./g, "||")
      .replace(/#/g, "||")
      .replace(/\$/g, "||")
      .replace(/\[/g, "||")
      .replace(/\]/g, "||")
      .replace(/\//g, "||");
  },
  emailKey(email: string): string {
    return this.filterPath(this.filterEmail(email));
  },
  validateEmail(value: string): boolean {
    //eslint-disable-next-line
    const valid = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)
    return valid;
  },
  getUserDisplayName(user: {
    displayName?: string;
    firstname?: string;
    lastname?: string;
  }): string {
    let displayName = "Anonymous";
    if (user) {
      if (user.displayName) {
        displayName = user.displayName;
        const pos = displayName.indexOf(" ");
        displayName =
          displayName.substring(0, pos) +
          " " +
          displayName.substring(pos + 1, pos + 2);
      } else {
        if (user.firstname && user.lastname) {
          displayName = user.firstname + " " + user.lastname[0];
        }
      }
    }
    return displayName;
  },
  noop(): any {
    // console.log("noop()");
    // console.log("no-empty-function");
  },
  transformValuesDto(
    values: { idx?: string },
    transformKey = "snakeCase"
  ): any {
    const newValues: LooseObject = {};
    type newKeys = keyof typeof newValues;
    if (_.isObject(values)) {
      type keys = keyof typeof values;
      for (const _key in values) {
        const key = _key as keys;
        let newKey: any = _key as newKeys;
        let value = values[key];
        let doTransform = false;
        if (_.isObject(value)) {
          value = this.transformValuesDto(value, transformKey);
          doTransform = true;
        } else {
          if (
            newKey.indexOf("valuesAndTraitsIsCustom") !== -1 ||
            newKey.indexOf("valuesAndTraitsVersionId") !== -1 ||
            newKey.indexOf("valuesAndTraitsScoringVersion") !== -1 ||
            newKey.indexOf("acceptableBehaviorsIsCustom") !== -1 ||
            newKey.indexOf("customSurveyIsCustom") !== -1 ||
            // newKey.indexOf("customSurveyCustomFields") !== -1 ||
            newKey.indexOf("customSurveyTitle") !== -1 ||
            newKey.indexOf("customSurveyDescription") !== -1
          ) {
            doTransform = true;
          }
        }
        if (doTransform) {
          // Skip transformation for locale keys (e.g., en_US, th_TH, es_ES)
          const localeCodes = this.getLocaleCodes();
          const isLocaleKey = localeCodes.includes(newKey);
          if (!isLocaleKey) {
            if (transformKey == "snakeCase" || transformKey == "camelCase") {
              newKey = _[transformKey](String(newKey));
            }
          }
        }
        newValues[newKey] = value;
      }
    }
    return newValues;
  },
  getLocaleDate(datetime?: string): string {
    if (!datetime) return "";

    const dateFormat = "MMMM d, y";
    const result = this.dateFormat(datetime, dateFormat);

    return result;
  },
  // @see https://stackoverflow.com/questions/3177836/how-to-format-time-since-xxx-e-g-4-minutes-ago-similar-to-stack-exchange-site
  _timeAgo(time: string | number | any): string {
    switch (typeof time) {
      case "number":
        break;
      case "string":
        time = +new Date(time);
        break;
      case "object":
        if (time.constructor === Date) time = time.getTime();
        break;
      default:
        time = +new Date();
    }
    const time_formats = [
      [60, "seconds", 1], // 60
      [120, "1 minute ago", "1 minute from now"], // 60*2
      [3600, "minutes", 60], // 60*60, 60
      [7200, "1 hour ago", "1 hour from now"], // 60*60*2
      [86400, "hours", 3600], // 60*60*24, 60*60
      [172800, "Yesterday", "Tomorrow"], // 60*60*24*2
      [604800, "days", 86400], // 60*60*24*7, 60*60*24
      [1209600, "Last week", "Next week"], // 60*60*24*7*4*2
      [2419200, "weeks", 604800], // 60*60*24*7*4, 60*60*24*7
      [4838400, "Last month", "Next month"], // 60*60*24*7*4*2
      [29030400, "months", 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
      [58060800, "Last year", "Next year"], // 60*60*24*7*4*12*2
      [2903040000, "years", 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
      [5806080000, "Last century", "Next century"], // 60*60*24*7*4*12*100*2
      [58060800000, "centuries", 2903040000], // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
    ];
    let seconds = (+new Date() - time) / 1000,
      token = "ago",
      list_choice = 1;
    if (seconds == 0) {
      return "Just now";
    }
    if (seconds < 0) {
      seconds = Math.abs(seconds);
      token = "from now";
      list_choice = 2;
    }
    let i = 0,
      format: (string | number | any)[];
    while ((format = time_formats[i++]))
      if (seconds < format[0]) {
        if (typeof format[2] == "string") return format[list_choice];
        else
          return (
            Math.floor(seconds / format[2]) + " " + format[1] + " " + token
          );
      }

    return time;
  },
  timeAgo(time: string | number | any): string {
    const result = this._timeAgo(time);

    // const words = [
    //   "seconds",
    //   "1 minute ago",
    //   "1 minute from now",
    //   "minutes",
    //   "1 hour ago",
    //   "1 hour from now",
    //   "hours",
    //   "Yesterday",
    //   "Tomorrow",
    //   "days",
    //   "Last week",
    //   "Next week",
    //   "weeks",
    //   "Last month",
    //   "Next month",
    //   "months",
    //   "Last year",
    //   "Next year",
    //   "years",
    //   "Last century",
    //   "Next century",
    //   "centuries",
    //   "Just now",
    //   "from now",
    // ];

    // const newWords: any = {};
    // for (const i in words) {
    //   const key = words[i].replaceAll(" ", "_");
    //   newWords[key] = words[i];
    // }

    // {"seconds":"seconds","1_minute_ago":"1 minute ago","1_minute_from_now":"1 minute from now","minutes":"minutes","1_hour_ago":"1 hour ago","1_hour_from_now":"1 hour from now","hours":"hours","Yesterday":"Yesterday","Tomorrow":"Tomorrow","days":"days","Last_week":"Last week","Next_week":"Next week","weeks":"weeks","Last_month":"Last month","Next_month":"Next month","months":"months","Last_year":"Last year","Next_year":"Next year","years":"years","Last_century":"Last century","Next_century":"Next century","centuries":"centuries","Just_now":"Just now","from_now":"from now"}

    return result;
  },
  // @see https://stackoverflow.com/questions/7860392/determine-if-string-is-in-base64-using-javascript
  isBase64(str: string): boolean {
    if (str === "" || str.trim() === "") {
      return false;
    }
    try {
      return btoa(atob(str)) == str;
    } catch (err) {
      return false;
    }
  },
  // @see https://stackoverflow.com/questions/12043187/how-to-check-if-hex-color-is-too-black
  //wc_hex_is_light(color: string): boolean {
  isLightColor(color: string): boolean {
    return brandTextColor(color) === "#000000";
  },
  getReferralCode(url = ""): string {
    const search = url.length ? url : window.location.search;
    const urlSearchParams = new URLSearchParams(
      search.startsWith("?") ? search : search ? `?${search}` : ""
    );
    const reserved = new Set(["tool", "email", "flow", "key", "plan", "purchase", "credits", "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "_gl", "landing_page", "last_page", "referrer", "ref", "source", "ab_hero_v1", "experiment_id", "experiment_variant", "checkout", "checkout_ref", "session_id"]);
    for (const key of urlSearchParams.keys()) {
      if (!reserved.has(key) && !key.startsWith("utm_")) {
        return key;
      }
    }
    return "";
  },
  ucfirst(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  },
  getMyCoreValuesRoute(user: { coreValues?: any }): any {
    let tags = "";
    if (user && Object.prototype.hasOwnProperty.call(user, "coreValues")) {
      const coreValues = user.coreValues;
      if (Array.isArray(coreValues)) {
        tags = coreValues.join(",");
      }
    }
    const to: any = { name: "CoreValues" };
    if (tags.length) {
      to.query = { tags: tags };
    }
    return to;
  },
  nl2br(value: string): string {
    return value.replace(/([^>])\n/g, "$1<br />\n");
  },
  generateId(length = 16): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },
  getPerformanceLevel: (score: number): string => {
    if (score >= 80) return "Excellent";
    if (score >= 70) return "Good";
    if (score >= 40) return "Fair";
    return "Poor";
  },
  extractAssessmentResultsAsText: (
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    data: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    focusUser: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    formTemplate: any
  ): string => {
    const summaries = data?.summaries || {};

    if (formTemplate) {
      // console.log(formTemplate, "formTemplate");
    }

    const candidateName =
      data.firstname && data.lastname
        ? `${data.firstname} ${data.lastname}`
        : data.firstname || data.email || "The candidate";
    const assessmentName = "Assessment";
    const companyName = focusUser?.companyName || "Organization";

    let textResults = `COMPREHENSIVE ASSESSMENT RESULTS FOR AI ANALYSIS\n`;
    textResults += `================================================================\n\n`;

    // Basic Information
    textResults += `CANDIDATE INFORMATION:\n`;
    textResults += `- Name: ${candidateName}\n`;
    textResults += `- Email: ${data.email || "N/A"}\n`;
    textResults += `- Assessment: ${assessmentName}\n`;
    textResults += `- Company: ${companyName}\n`;
    textResults += `- Assessment Date: ${
      data.created_at ? new Date(data.created_at).toLocaleDateString() : "N/A"
    }\n`;
    textResults += `- Status: ${data.status || "N/A"}\n\n`;

    // Assessment Overview
    textResults += `ASSESSMENT OVERVIEW:\n`;
    textResults += `This comprehensive assessment evaluates candidates across multiple dimensions to determine cultural fit, `;
    textResults += `skills alignment, and overall suitability for the organization.\n\n`;

    // Overall Score
    if (summaries.overall) {
      textResults += `OVERALL ASSESSMENT SCORE: ${Math.round(
        summaries.overall
      )}%\n`;
      textResults += `Performance Level: ${helpers.getPerformanceLevel(
        summaries.overall
      )}\n`;
      textResults += `Overall Cultural Alignment: ${Math.round(
        summaries.overall
      )}%\n\n`;
    }

    // Values Alignment v4: coverage of the company's own values
    if (summaries.values_and_traits && summaries.values_and_traits.version === "v4") {
      const va = summaries.values_and_traits;
      const cardName = (id: string) => (va.card_names && va.card_names[id]) || id;
      // Stored lists can come back as objects keyed "0", "1", ...
      const listOf = (v: any): any[] =>
        Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];
      textResults += `VALUES ALIGNMENT ASSESSMENT:\n`;
      textResults += typeof va.overall === "number"
        ? `- Overall Score: ${va.overall}% (${va.band})\n`
        : `- Overall Score: not scored (none of the company's values maps to a value card)\n`;
      const picks = listOf(va.picks).map(cardName);
      if (picks.length) {
        textResults += `- Candidate's values${va.ranked ? " (most important first)" : ""}: ${picks.join(", ")}\n`;
      }
      for (const row of listOf(va.per_value)) {
        const status =
          row.credit === null ? "not scored (no value card expresses it; explore in the interview)" :
            row.credit === 1 ? `shared through ${cardName(row.via_card)}` :
              row.credit === 0.5 ? `partly shared through ${cardName(row.via_card)}` :
                "not shared";
        textResults += `- Company value "${row.name}": ${status}\n`;
      }
      textResults += `\n`;
    } else if (summaries.values_and_traits) {
      textResults += `VALUES ALIGNMENT ASSESSMENT:\n`;
      textResults += `- Overall Score: ${summaries.values_and_traits.overall}%\n`;
      if (summaries.values_and_traits.defining_your_values) {
        textResults += `- Defining Values Score: ${summaries.values_and_traits.defining_your_values}%\n`;
      }
      if (summaries.values_and_traits.identifying_key_traits) {
        textResults += `- Key Traits Score: ${summaries.values_and_traits.identifying_key_traits}%\n`;
      }
      textResults += `\n`;
    }

    // Culture Profile
    if (summaries.oaic_archetypes) {
      const oaicCompletion = getNormalizedOaicCompletion(
        summaries.oaic_archetypes.completion || {}, summaries.oaic_archetypes
      );
      const completedDimensions = new Set(oaicCompletion.completed);
      const incompleteDimensions = oaicCompletion.incomplete;
      const hasCompletedDimensions =
        Number(oaicCompletion.completed_count || completedDimensions.size) > 0;

      textResults += `CULTURE PROFILE ASSESSMENT:\n`;
      textResults += `- Culture Profile Score: ${
        hasCompletedDimensions
          ? `${Math.round(summaries.oaic_archetypes.overall)}%`
          : "Incomplete"
      }\n`;
      textResults += `- Culture Fit Level: ${
        hasCompletedDimensions
          ? helpers.getPerformanceLevel(summaries.oaic_archetypes.overall || 0)
          : "Incomplete"
      }\n`;
      if (oaicCompletion.total) {
        textResults += `- Based on ${oaicCompletion.completed_count || 0} of ${
          oaicCompletion.total
        } completed sections\n`;
      }

      if (summaries.oaic_archetypes.diff) {
        const dimensions = summaries.oaic_archetypes.diff;
        textResults += `Cultural Dimensions:\n`;
        if (
          completedDimensions.has("dominant_characteristics") &&
          dimensions.dominant_characteristics !== undefined
        ) {
          textResults += `  • Dominant Characteristics: ${Math.round(
            dimensions.dominant_characteristics
          )}%\n`;
        }
        if (
          completedDimensions.has("organizational_leadership") &&
          dimensions.organizational_leadership !== undefined
        ) {
          textResults += `  • Organizational Leadership: ${Math.round(
            dimensions.organizational_leadership
          )}%\n`;
        }
        if (
          completedDimensions.has("management_of_employees") &&
          dimensions.management_of_employees !== undefined
        ) {
          textResults += `  • Management of Employees: ${Math.round(
            dimensions.management_of_employees
          )}%\n`;
        }
        if (
          completedDimensions.has("organization_glue") &&
          dimensions.organization_glue !== undefined
        ) {
          textResults += `  • Organization Glue: ${Math.round(
            dimensions.organization_glue
          )}%\n`;
        }
        if (
          completedDimensions.has("strategic_emphases") &&
          dimensions.strategic_emphases !== undefined
        ) {
          textResults += `  • Strategic Emphases: ${Math.round(
            dimensions.strategic_emphases
          )}%\n`;
        }
        if (
          completedDimensions.has("criteria_of_success") &&
          dimensions.criteria_of_success !== undefined
        ) {
          textResults += `  • Criteria of Success: ${Math.round(
            dimensions.criteria_of_success
          )}%\n`;
        }
      }
      if (incompleteDimensions.length > 0) {
        textResults += `  • Incomplete: ${incompleteDimensions
          .map((dimension: string) =>
            String(dimension)
              .split("_")
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join(" ")
          )
          .join(", ")}\n`;
      }
      textResults += `\n`;
    }

    // Human Skills
    if (summaries.human_skills) {
      textResults += `HUMAN SKILLS ASSESSMENT:\n`;
      textResults += `- Overall Score: ${summaries.human_skills.overall}%\n`;

      if (summaries.human_skills.human_skills) {
        const skills = summaries.human_skills.human_skills;
        textResults += `Skill Breakdown:\n`;
        if (skills.written_communication) {
          textResults += `  • Written Communication: ${Math.round(
            skills.written_communication.score
          )}%\n`;
        }
        if (skills.empathy) {
          textResults += `  • Empathy: ${Math.round(skills.empathy.score)}%\n`;
        }
        if (skills.situational_awareness) {
          textResults += `  • Situational Awareness: ${Math.round(
            skills.situational_awareness.score
          )}%\n`;
        }
        if (skills.critical_thinking) {
          textResults += `  • Critical Thinking: ${Math.round(
            skills.critical_thinking.score
          )}%\n`;
        }
      }
      textResults += `\n`;
    }

    // Logic Test
    if (summaries.logic_test) {
      textResults += `LOGIC TEST ASSESSMENT:\n`;
      textResults += `- Overall Score: ${summaries.logic_test.overall}%\n`;

      if (summaries.logic_test.candidate_avg) {
        const logicSkills = summaries.logic_test.candidate_avg;
        textResults += `Cognitive Skills:\n`;
        if (logicSkills.pattern_recognition !== undefined) {
          textResults += `  • Pattern Recognition: ${Math.round(
            logicSkills.pattern_recognition
          )}%\n`;
        }
        if (logicSkills.logical_reasoning !== undefined) {
          textResults += `  • Logical Reasoning: ${Math.round(
            logicSkills.logical_reasoning
          )}%\n`;
        }
        if (logicSkills.data_interpretation !== undefined) {
          textResults += `  • Data Interpretation: ${Math.round(
            logicSkills.data_interpretation
          )}%\n`;
        }
        if (logicSkills.problem_resolving !== undefined) {
          textResults += `  • Problem Resolving: ${Math.round(
            logicSkills.problem_resolving
          )}%\n`;
        }
      }
      textResults += `\n`;
    }

    // Acceptable Behaviors
    if (summaries.acceptable_behaviors) {
      textResults += `ACCEPTABLE BEHAVIORS ASSESSMENT:\n`;
      textResults += `- Overall Alignment: ${summaries.acceptable_behaviors.overall}%\n\n`;
    }

    // Work Style
    if (
      summaries.work_style_identifier &&
      summaries.work_style_identifier.overall
    ) {
      textResults += `WORK STYLE ASSESSMENT:\n`;
      const workStyle = summaries.work_style_identifier.overall;
      if (workStyle.people !== undefined) {
        textResults += `- People-focused: ${Math.round(
          workStyle.people * 100
        )}%\n`;
      }
      if (workStyle.process !== undefined) {
        textResults += `- Process-focused: ${Math.round(
          workStyle.process * 100
        )}%\n`;
      }
      if (workStyle.product !== undefined) {
        textResults += `- Product-focused: ${Math.round(
          workStyle.product * 100
        )}%\n`;
      }
      textResults += `\n`;
    }

    // Big Five Personality Assessment
    if (summaries.big_five_personality || summaries.big5) {
      textResults += `BIG FIVE PERSONALITY ASSESSMENT:\n`;

      let bigFive;
      // Check for new format first, then fall back to existing format
      if (summaries.big_five_personality) {
        bigFive = summaries.big_five_personality;
      } else if (summaries.big5 && summaries.big5.result) {
        bigFive = summaries.big5.result;
      }

      if (bigFive) {
        if (bigFive.overall !== undefined) {
          textResults += `- Overall Personality Score: ${Math.round(
            bigFive.overall
          )}%\n`;
        }

        // Handle new format: bigFive.dimensions
        if (bigFive.dimensions) {
          textResults += `Personality Dimensions:\n`;

          if (bigFive.dimensions.openness) {
            textResults += `  • Openness to Experience: ${Math.round(
              bigFive.dimensions.openness.score || bigFive.dimensions.openness
            )}%\n`;
            textResults += `    (Creativity, intellectual curiosity, openness to new ideas)\n`;
          }

          if (bigFive.dimensions.conscientiousness) {
            textResults += `  • Conscientiousness: ${Math.round(
              bigFive.dimensions.conscientiousness.score ||
                bigFive.dimensions.conscientiousness
            )}%\n`;
            textResults += `    (Organization, self-discipline, goal-directed behavior)\n`;
          }

          if (bigFive.dimensions.extraversion) {
            textResults += `  • Extraversion: ${Math.round(
              bigFive.dimensions.extraversion.score ||
                bigFive.dimensions.extraversion
            )}%\n`;
            textResults += `    (Sociability, assertiveness, energetic approach to social situations)\n`;
          }

          if (bigFive.dimensions.agreeableness) {
            textResults += `  • Agreeableness: ${Math.round(
              bigFive.dimensions.agreeableness.score ||
                bigFive.dimensions.agreeableness
            )}%\n`;
            textResults += `    (Trust, altruism, kindness, cooperation in relationships)\n`;
          }

          if (bigFive.dimensions.neuroticism) {
            textResults += `  • Neuroticism: ${Math.round(
              bigFive.dimensions.neuroticism.score ||
                bigFive.dimensions.neuroticism
            )}%\n`;
            textResults += `    (Emotional stability vs. tendency toward negative emotions)\n`;
          }
        }
        // Handle existing format: direct property access for dimensions
        else {
          textResults += `Personality Dimensions:\n`;

          if (bigFive.openness !== undefined) {
            textResults += `  • Openness to Experience: ${Math.round(
              bigFive.openness
            )}%\n`;
            textResults += `    (Creativity, intellectual curiosity, openness to new ideas)\n`;
          }

          if (bigFive.conscientiousness !== undefined) {
            textResults += `  • Conscientiousness: ${Math.round(
              bigFive.conscientiousness
            )}%\n`;
            textResults += `    (Organization, self-discipline, goal-directed behavior)\n`;
          }

          if (bigFive.extraversion !== undefined) {
            textResults += `  • Extraversion: ${Math.round(
              bigFive.extraversion
            )}%\n`;
            textResults += `    (Sociability, assertiveness, energetic approach to social situations)\n`;
          }

          if (bigFive.agreeableness !== undefined) {
            textResults += `  • Agreeableness: ${Math.round(
              bigFive.agreeableness
            )}%\n`;
            textResults += `    (Trust, altruism, kindness, cooperation in relationships)\n`;
          }

          if (bigFive.neuroticism !== undefined) {
            textResults += `  • Neuroticism: ${Math.round(
              bigFive.neuroticism
            )}%\n`;
            textResults += `    (Emotional stability vs. tendency toward negative emotions)\n`;
          }
        }
      }
      textResults += `\n`;
    }

    // Time Analysis
    if (data.section_spent_times) {
      textResults += `TIME ANALYSIS:\n`;
      const totalTime = Object.values(data.section_spent_times).reduce(
        (sum: number, time: any) => sum + Number(time),
        0
      );
      const totalMinutes = Math.round(Number(totalTime) / 60);
      textResults += `Total Assessment Time: ${totalMinutes} minutes\n\n`;
    }

    textResults += `================================================================\n`;
    textResults += `END OF ASSESSMENT ANALYSIS\n`;

    return textResults;
  },
  sanitizeId: (str: string): string => {
    if (!str || str.trim() === "") return "_";

    const replaceAllPolyfill = (
      str: string,
      search: string,
      replacement: string
    ) => {
      return str.split(search).join(replacement);
    };

    return replaceAllPolyfill(str, ".", "||")
      .replace(/\s+/g, "_")
      .replace(/[/\\#[\]()$%^&*<>:"?{|}~`]/g, "-");
  },
  displayTextWithDelay: async (
    text: string,
    cb = (text: string) => {
      console.log(text);
    },
    maxCharsPerBatch = 120,
    delayMs = 180
  ): Promise<void> => {
    let accumulatedText = "";

    for (let i = 0; i < text.length; i += maxCharsPerBatch) {
      const batch = text.slice(i, i + maxCharsPerBatch);
      accumulatedText += batch;

      // Display the accumulated text (streaming effect)
      cb(accumulatedText);

      // Wait for the specified delay
      if (i + maxCharsPerBatch < text.length) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  },
};
export default helpers;
